import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface ResendVerificationLinkProps {
  email: string;
  /**
   * URL to redirect to after the user clicks the verification email link.
   * Defaults to the /auth/confirm callback which then forwards to `/`.
   * Callers can pass a more specific destination via ?next=...
   */
  redirectUrl?: string;
  variant?: "default" | "link" | "outline" | "secondary" | "ghost" | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export function ResendVerificationLink({
  email,
  redirectUrl = `${window.location.origin}/auth/confirm?next=%2F`,
  variant = "link",
  size = "sm",
  className = "",
}: ResendVerificationLinkProps) {
  const { t } = useLanguage();
  const [isResending, setIsResending] = useState(false);

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(t.auth.verificationEmailSentToast);
      }
    } catch {
      toast.error(t.auth.failedToResendEmail);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleResendVerification}
      disabled={isResending}
      className={className}
    >
      <Mail className="mr-2 h-4 w-4" />
      {isResending ? t.common.sending : t.auth.resendVerificationEmail}
    </Button>
  );
}
